import React from 'react';
import { Typography, Card, Button, Modal, message, Tag, Alert } from 'antd';
import { SettingOutlined, DeleteOutlined, UserOutlined, WarningFilled, LinkOutlined, ReloadOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { tokens } from '../theme/tokens';
import { useSocialContext } from '../context/social';
import { useProfileCompleteness, SCOPE_LABELS } from '../hooks/use-profile-completeness';
import { goFullReload } from '../utils/nav';
import { PaymentDataForm } from '../components/payment-data-form';

const { Title, Text } = Typography;

export const SettingsPage: React.FC = () => {
  const { deleteAllData, logout, connectProvider } = useSocialContext();
  const { loading: profileLoading, paymentComplete, permissionsComplete, missingScopesUnion } = useProfileCompleteness();
  const [messageApi, contextHolder] = message.useMessage();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const handleDeleteData = async () => {
    setDeleting(true);
    try {
      await deleteAllData();
      setDeleteModalOpen(false);
      messageApi.success('Todos tus datos fueron eliminados');
      setTimeout(() => { logout(); goFullReload('/'); }, 1200);
    } catch {
      messageApi.error('Error al eliminar los datos. Intentá de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 24px' }}>
      {contextHolder}

      {/* Page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <SettingOutlined style={{ fontSize: 22, color: tokens.colors.brand.blue }} />
        <Title level={3} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
          Configuración
        </Title>
      </div>

      {/* Cartel CRÍTICO genérico (feedback 2026-07-13): unifica el aviso de "perfil incompleto"
          — sea por permisos de TikTok o por datos personales. En ROJO (error) cuando faltan
          permisos de TikTok (sin ellos NO se puede usar la app); en amarillo cuando solo faltan
          los datos de pago. Detalla qué falta. */}
      {!profileLoading && (!paymentComplete || !permissionsComplete) && (
        <Alert
          type={!permissionsComplete ? 'error' : 'warning'}
          showIcon
          icon={!permissionsComplete ? <ExclamationCircleFilled /> : <WarningFilled />}
          message={<span style={{ fontWeight: 700 }}>Faltan datos críticos en tu perfil</span>}
          description={
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
              {!permissionsComplete && (
                <span>
                  Tu cuenta de TikTok no otorgó todos los permisos.{' '}
                  <strong>Sin ellos no vas a poder usar la app.</strong> Reconectala más abajo.
                </span>
              )}
              {!paymentComplete && (
                <span>
                  Faltan datos para poder pagarte los premios (datos personales, país o método
                  de cobro). Completalos en Datos Personales.
                </span>
              )}
            </div>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
        />
      )}

      {/* Datos Personales — nombre, correo, país, método de cobro (Mercado Pago o
          transferencia bancaria) + términos y mayoría de edad. Mismo form que el popup de
          "Completa tus datos personales", pero con la sección de cobro (withPaymentData:
          el popup solo pide lo esencial). Estos datos se usan para pagarte los premios. */}
      <Card
        style={{
          borderRadius: 14,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          marginBottom: 20,
        }}
        styles={{ body: { padding: 24 } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserOutlined style={{ fontSize: 20, color: '#7c3aed' }} />
          </div>
          <Title level={4} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
            Datos Personales
          </Title>
          {!profileLoading && !paymentComplete && (
            <Tag icon={<WarningFilled />} color='warning' style={{ marginLeft: 4, marginBottom: 0 }}>
              Falta completar
            </Tag>
          )}
        </div>
        <PaymentDataForm withPaymentData submitLabel='Guardar cambios' emailLabel='Correo electrónico' />
      </Card>

      {/* Conexión de TikTok — permisos otorgados en el OAuth. Si el usuario destildó
          permisos en la pantalla de TikTok, faltan capacidades (ej. sin user.info.profile
          no hay @usuario). "Reconectar" re-abre el OAuth pidiendo TODOS los permisos; al
          volver, el callback actualiza la cuenta con los scopes nuevos. */}
      <Card
        style={{
          borderRadius: 14,
          border: !permissionsComplete ? '1.5px solid #ff4d4f' : '1px solid rgba(0,0,0,0.08)',
          boxShadow: !permissionsComplete ? '0 2px 16px rgba(255,77,79,0.18)' : '0 2px 12px rgba(0,0,0,0.04)',
          marginBottom: 20,
        }}
        styles={{ body: { padding: 24 } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: !permissionsComplete ? '#fff1f0' : '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LinkOutlined style={{ fontSize: 20, color: !permissionsComplete ? '#cf1322' : '#0284c7' }} />
          </div>
          <Title level={4} style={{ margin: 0, color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
            Conexión de TikTok
          </Title>
          {!profileLoading && !permissionsComplete && (
            <Tag icon={<ExclamationCircleFilled />} color='error' style={{ marginLeft: 4, marginBottom: 0, fontWeight: 600 }}>
              Faltan permisos
            </Tag>
          )}
        </div>

        {permissionsComplete ? (
          <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 13 }}>
            Tu cuenta de TikTok otorgó todos los permisos necesarios. ✓
          </Text>
        ) : (
          <>
            <Text style={{ color: '#cf1322', fontSize: 13, display: 'block', marginBottom: 12 }}>
              Le faltan permisos a tu cuenta de TikTok. <strong>Sin ellos no vas a poder usar la
              app</strong>: no podemos mostrar tu usuario (@handle) ni publicar en tu nombre.
              Reconectá tu cuenta y, en la pantalla de TikTok, <strong>dejá todos los permisos
              tildados</strong>.
            </Text>
            <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: tokens.colors.primary.neutral[600], fontSize: 13 }}>
              {missingScopesUnion.map(s => (
                <li key={s}>{SCOPE_LABELS[s] || s}</li>
              ))}
            </ul>
            <Button
              type='primary'
              danger
              icon={<ReloadOutlined />}
              onClick={() => connectProvider('tiktok')}
              style={{ fontWeight: 600 }}
            >
              Reconectar y dar permisos
            </Button>
          </>
        )}
      </Card>

      {/* Data deletion section */}
      <Card
        style={{
          borderRadius: 14,
          border: `1px solid rgba(220,38,38,0.2)`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        }}
        styles={{ body: { padding: '20px 24px' } }}
      >
        <Title level={5} style={{ margin: '0 0 4px', color: tokens.colors.primary.neutral[800] }}>
          Mis datos
        </Title>
        <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 13, display: 'block', marginBottom: 16 }}>
          Eliminá permanentemente todas tus cuentas conectadas, publicaciones y métricas.
          Esta acción no se puede deshacer.
        </Text>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => setDeleteModalOpen(true)}
          style={{ fontWeight: 600 }}
        >
          Eliminar todos mis datos
        </Button>
      </Card>

      {/* Confirm delete modal */}
      <Modal
        title='¿Estás seguro?'
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        footer={[
          <Button key='cancel' onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
            Cancelar
          </Button>,
          <Button
            key='delete'
            danger
            type='primary'
            loading={deleting}
            icon={<DeleteOutlined />}
            onClick={handleDeleteData}
          >
            Sí, eliminar todo
          </Button>,
        ]}
      >
        <Text style={{ color: tokens.colors.primary.neutral[600] }}>
          Se eliminarán todas tus cuentas conectadas, publicaciones y métricas. Tu sesión se cerrará
          automáticamente. Esta acción no se puede deshacer.
        </Text>
      </Modal>
    </div>
  );
};
